// === Smooth anchor scrolling with GSAP (fallback included) ===
// Tweak these if you like:
const SCROLL_DURATION = 1.5; // seconds (≈1500ms)
const EASE = "power2.inOut"; // roughly easeInOutCubic
const HEADER_OFFSET = 0;     // set to your fixed header height if needed

// Helper: compute target Y with optional offset
function getTargetY(el) {
  const rect = el.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
  const y = scrollTop + rect.top - HEADER_OFFSET;
  return Math.max(0, y);
}

// Smooth scroll to a Y position (GSAP if available, else rAF)
function smoothScrollTo(targetY, onDone) {
  if (window.gsap && gsap.plugins && gsap.plugins.ScrollToPlugin) {
    gsap.to(window, {
      duration: SCROLL_DURATION,
      ease: EASE,
      scrollTo: { y: targetY, autoKill: false },
      onComplete: () => { if (onDone) onDone(); }
    });
    return;
  }

  const startY = window.scrollY || window.pageYOffset;
  const delta = targetY - startY;
  const startTime = performance.now();
  const durMs = SCROLL_DURATION * 1000;

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function step(now) {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / durMs);
    const eased = easeInOutCubic(t);
    window.scrollTo(0, startY + delta * eased);
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      window.scrollTo(0, targetY); // snap to exact
      if (onDone) onDone();
    }
  }

  requestAnimationFrame(step);
}

// Robust element lookup from a hash ("#id")
// Handles IDs that start with digits and percent-encoding
function findTargetFromHash(hash) {
  if (!hash || hash === "#") return null;
  let id;
  try { id = decodeURIComponent(hash.slice(1)); }
  catch { id = hash.slice(1); }

  // Prefer ID match (no CSS escaping needed)
  let target = document.getElementById(id);
  if (target) return target;

  // Fallback: named anchors
  try {
    const esc = (window.CSS && CSS.escape) ? CSS.escape(id) : id.replace(/"/g, '\\"');
    target = document.querySelector(`[name="${esc}"]`);
  } catch {
    target = null;
  }
  return target;
}

// Click handler
function onAnchorClick(e) {
  // Respect modifier keys / non-left clicks
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

  const link = e.currentTarget;
  const rawHref = link.getAttribute("href") || "";

  // Safely normalize link URL (supports "#id", "/#id", absolute)
  let linkURL;
  try {
    linkURL = new URL(rawHref, location.origin + location.pathname);
  } catch {
    return; // malformed; ignore
  }

  const hash = linkURL.hash;
  if (!hash || hash === "#") return;

  // Only intercept if the target exists on this page
  const target = findTargetFromHash(hash);
  if (!target) {
    // No target on this document -> allow normal navigation
    return;
  }

  // We have a target on this page; prevent default and smooth-scroll
  e.preventDefault();

  const targetY = getTargetY(target);
  const dest = rawHref.startsWith("/#") || rawHref.startsWith(location.origin + "/#")
    ? rawHref
    : hash;

  smoothScrollTo(targetY, () => {
    // Update hash without jumping
    history.pushState(null, "", dest);
  });
}

// Bind to supported hash links
function bindSmoothAnchors(root = document) {
  const selector = [
    'a[href^="#"]',
    'a[href^="/#"]',
    `a[href^="${location.origin}/#"]`
  ].join(", ");

  const anchors = root.querySelectorAll(selector);
  anchors.forEach(a => {
    a.removeEventListener("click", onAnchorClick);
    a.addEventListener("click", onAnchorClick);
  });
}

// DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => bindSmoothAnchors());
} else {
  bindSmoothAnchors();
}

// Shopify sections often re-render DOM; re-bind after loads/unloads
document.addEventListener("shopify:section:load", () => bindSmoothAnchors());
document.addEventListener("shopify:section:unload", () => bindSmoothAnchors());

// (Optional) If your app dynamically swaps HTML (AJAX), call bindSmoothAnchors(container)
